<template>
  <Modal
    v-model:visible="isVisible"
    class="invite-carriers-to-spot-market-modal"
    :header="t('carrierPortal.spotMarket.requestBid')"
    :close-on-escape="!savedTemplateModal.visible"
    @keyup.enter="onSave"
    @keyup.esc="onCancel"
  >
    <i18n-t
      v-if="showMissingShipmentSpecIdWarning"
      id="invite-instructions"
      keypath="carrierPortal.spotMarket.missingShipmentSpecIdWarning"
      tag="p"
      class="m-0"
    >
      <template #buttonName>
        <i>{{ t('carrierPortal.spotMarket.requestBid') }}</i>
      </template>
    </i18n-t>
    <p v-else id="invite-instructions">
      {{
        t('carrierPortal.spotMarket.requestBidForParam', {
          count: numLoads,
          param: paramString,
        })
      }}
    </p>

    <form
      v-if="!showMissingShipmentSpecIdWarning"
      aria-labelledby="invite-instructions"
      class="invite-carriers-to-spot-market-modal-form"
    >
      <InputWrapper
        v-if="$hasAuthority('spotMarketRequestTemplate', 'read')"
        v-slot="{ ariaProps, inputId }"
        input-id="invite-carriers-to-spot-market-modal-template"
        :label="t('templates.templates')"
        class="template-selector"
      >
        <RygenDropdown
          :id="inputId"
          v-model="selectedTemplate"
          v-bind="ariaProps"
          :options="spotMarketRequestTemplates.data"
          :loading="spotMarketRequestTemplates.loading"
          :placeholder="t('templates.templates')"
          append-to="body"
          option-label="name"
        >
          <template #option="{ option }">
            <div
              class="w-full flex flex-gap-1 justify-content-between align-items-center"
            >
              {{ option.name }}

              <div
                class="flex flex-gap-1 justify-content-end align-items-center"
              >
                <RygenButton
                  v-if="option.defaultTemplate"
                  :disabled="
                    !$hasAuthority('spotMarketRequestTemplate', 'update')
                  "
                  mode="tertiary"
                  icon="pi pi-star-fill"
                  :aria-label="t('templates.removeDefaultTemplate')"
                  @mousedown.stop="removeDefaultTemplate(option)"
                />
                <RygenButton
                  v-else
                  :disabled="
                    !$hasAuthority('spotMarketRequestTemplate', 'update')
                  "
                  mode="tertiary"
                  icon="pi pi-star"
                  :aria-label="t('templates.setDefaultTemplate')"
                  @mousedown.stop="setDefaultTemplate(option)"
                />
                <RygenButton
                  v-if="$hasAuthority('spotMarketRequestTemplate', 'delete')"
                  mode="tertiary"
                  icon="pi pi-trash"
                  :aria-label="
                    t('common.deleteParam', { param: `'${option.name}'` })
                  "
                  :severity="Severity.DANGER"
                  @mousedown.stop="onDeleteTemplate(option.id)"
                />
              </div>
            </div>
          </template>
        </RygenDropdown>
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="invite-carriers-to-spot-market-modal-carriers"
        :label="t('carriers.carriers', 2)"
        :errors="v$.scacs.$errors"
      >
        <CarrierMultiSelect
          v-bind="ariaProps"
          :id="inputId"
          v-model="v$.scacs.$model"
          class="flex-grow-1"
          :class="[inputClasses]"
          :invalid="invalid"
          has-email-config
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="invite-carriers-to-spot-market-modal-mode"
        :label="t('shipments.modes')"
        :errors="v$.mode.$errors"
      >
        <RygenDropdown
          :id="inputId"
          v-model="internalValue.mode"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :options="availableModes"
          :placeholder="t('shipments.modes')"
          append-to="body"
          option-label="label"
          option-value="value"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="invite-carriers-to-spot-market-modal-service-level"
        :errors="v$.serviceType.$errors"
        :label="t('carriers.serviceLevels')"
      >
        <RygenDropdown
          :id="inputId"
          v-model="internalValue.serviceType"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :loading="serviceLevels.loading"
          :options="serviceLevels.data"
          :placeholder="t('carriers.serviceLevels')"
          append-to="body"
          option-label="name"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="invite-carriers-to-spot-market-modal-equipment-types"
        :errors="v$.equipmentType.$errors"
        :label="t('common.paramTypes', { param: t('referenceData.equipment') })"
      >
        <RygenDropdown
          :id="inputId"
          v-model="internalValue.equipmentType"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :loading="equipmentTypes.loading"
          :options="equipmentTypes.data"
          :placeholder="
            t('common.paramTypes', { param: t('referenceData.equipment') })
          "
          append-to="body"
          option-label="name"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId }"
        input-id="invite-carriers-to-spot-market-modal-expriation-duration"
        :label="t('common.expiration')"
        optional
      >
        <DurationSelect
          :id="inputId"
          v-model="internalValue.expirationDuration"
          v-bind="ariaProps"
          :class="inputClasses"
          :possible-durations="expirationOptions"
          :placeholder="t('common.expiration')"
          append-to="body"
          show-clear
        />
      </InputWrapper>

      <div class="flex flex-gap-3">
        <WrappedSwitch
          id="invite-carriers-to-spot-market-modal-require-fuel"
          v-model="internalValue.requireFuel"
          :label="t('common.requireFuel')"
        />
        <WrappedSwitch
          id="invite-carriers-to-spot-market-modal-include-carrier-notes"
          v-model="internalValue.includeCarrierNotes"
          :label="t('orders.paramNotes', { param: t('carriers.carriers') })"
        />
        <WrappedSwitch
          id="invite-carriers-to-spot-market-modal-include-location-notes"
          v-model="internalValue.includeLocationNotes"
          :label="t('orders.paramNotes', { param: t('locations.locations') })"
          :disabled="!internalValue.includeCarrierNotes"
        />
      </div>

      <EditCarrierNotesInput
        v-if="showEditableNotes"
        v-model:internal-carrier-notes="internalCarrierNotes"
        v-model:is-editing-notes="isEditingCarrierNotes"
        :carrier-notes="carrierNotes"
      />

      <SpotMarketRequestTemplateUpliftEdit
        v-if="canViewUplift"
        id="invite-carriers-to-spot-market-modal-uplift-edit"
        v-model="internalValue"
        class="top-border"
      />
    </form>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        v-if="
          hasUpdateOrCreateTemplatePermission &&
          !showMissingShipmentSpecIdWarning
        "
        mode="secondary"
        icon="pi pi-save"
        :label="t('common.saveAsParam', { param: t('templates.templates') })"
        :loading="spotMarketRequestTemplates.loading"
        @click="onSaveTemplate"
      />
      <RygenButton
        v-if="!showMissingShipmentSpecIdWarning"
        mode="primary"
        icon="pi pi-check"
        :disabled="
          disabled ||
          v$.$invalid ||
          isEditingCarrierNotes ||
          isEditingLocationNotes
        "
        :label="t('common.confirm')"
        @click="onSave"
      />
    </template>

    <SpotMarketRequestTemplateModal
      v-model:visible="savedTemplateModal.visible"
      :template="savedTemplateModal.template"
      @save="onSaveTemplateConfirm"
    />
  </Modal>
</template>

<script lang="ts" setup generic="T extends InviteCommand">
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  minLength,
  required,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import {
  ExpirationDuration,
  Mode,
  Severity,
  SpotMarketRequestTemplate,
} from '@rygen/rygen-ui/model';

import { useSpotMarketRequestTemplates } from '@/composables';
import {
  InviteCommand,
  ShipmentInviteCarriersToSpotMarketCommand,
  ShipmentSpecInviteCarriersToSpotMarketCommand,
} from '@/model';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';
import { useServiceLevelStore } from '@/stores/ServiceLevel';

import CarrierMultiSelect from '@/components/carrier/CarrierMultiSelect.vue';
import EditCarrierNotesInput from '@/components/carrierPortal/EditCarrierNotesInput.vue';
import SpotMarketRequestTemplateModal from '@/components/carrierPortal/SpotMarketRequestTemplateModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import DurationSelect from '@rygen/rygen-ui/components/form/DurationSelect.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import SpotMarketRequestTemplateUpliftEdit from '@rygen/rygen-ui/components/spotMarket/SpotMarketRequestTemplateUpliftEdit.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  inviteCommand: {
    type: Object as PropType<T>,
    default: () => new ShipmentInviteCarriersToSpotMarketCommand(),
  },
  carrierNotes: {
    type: String,
    // Don't set empty string because then pages will see it change on save
    default: () => undefined,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  showCarrierNotes: {
    type: Boolean,
    default: () => false,
  },
  canViewUplift: {
    type: Boolean,
    default: () => false,
  },
});
const {
  inviteCommand,
  carrierNotes,
  disabled,
  showCarrierNotes,
  canViewUplift,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'submit', inviteCommand: T, carrierNotes?: string): void;
}>();

const { equipmentTypes, loadEquipmentTypes } = useEquipmentTypeStore();
const { t } = useI18n();
const { serviceLevels, loadServiceLevels } = useServiceLevelStore();
const {
  createSpotMarketRequestTemplate,
  defaultTemplate,
  deleteSpotMarketRequestTemplate,
  loadSpotMarketRequestTemplates,
  removeDefaultSpotMarketRequestTemplate,
  setDefaultSpotMarketRequestTemplate,
  spotMarketRequestTemplates,
  updateSpotMarketRequestTemplate,
} = useSpotMarketRequestTemplates();
const { uid } = useUid('invite-carriers-to-spot-market-modal');

const Clazz = Object.getPrototypeOf(inviteCommand.value).constructor;
const internalValue: Ref<T> = ref(new Clazz());
const rules = {
  scacs: {
    required: required(t('carriers.carriers')),
    minLength: minLength(1, t('carriers.carriers', 2)),
  },
  mode: {
    required: required(t('shipments.modes')),
  },
  serviceType: {
    required: required(t('carriers.serviceLevels')),
  },
  equipmentType: {
    required: required(
      t('common.paramTypes', { param: t('referenceData.equipment') }),
    ),
  },
};
const { v$, validate } = useValidations(internalValue, rules, uid);

const availableModes = mapEnumToTranslatedDropdownValues(Mode, 'Mode');

const selectedTemplate: Ref<SpotMarketRequestTemplate | undefined> = ref();
const savedTemplateModal = ref({
  visible: false,
  template: new SpotMarketRequestTemplate(),
});
const internalCarrierNotes = ref(carrierNotes?.value);
const isEditingCarrierNotes = ref(false);
const isEditingLocationNotes = ref(false);

const expirationOptions: ExpirationDuration[] = [
  ExpirationDuration.PT30M,
  ExpirationDuration.PT1H,
  ExpirationDuration.PT2H,
  ExpirationDuration.PT4H,
  ExpirationDuration.PT12H,
  ExpirationDuration.PT24H,
  ExpirationDuration.PT168H,
];

// Vue's generic support is still a bit off. Typescript should be able to infer
// this type, and we should be able to use the getter directly in the template.
const numLoads: Ref<number> = toRef(() => inviteCommand.value.numLoads);

const showEditableNotes = computed(
  () => showCarrierNotes.value && internalValue.value.includeCarrierNotes,
);

const paramString = computed(() => {
  return ['ShipmentSpec', 'Order'].includes(inviteCommand.value.type)
    ? t('orders.orders', numLoads.value)
    : t('shipments.shipments', numLoads.value);
});

const showMissingShipmentSpecIdWarning = computed(() => {
  if (
    !ShipmentSpecInviteCarriersToSpotMarketCommand.isShipmentSpecInviteCarriersToSpotMarketCommand(
      internalValue.value,
    )
  ) {
    return false;
  }

  const typedValue =
    internalValue.value as ShipmentSpecInviteCarriersToSpotMarketCommand;
  return !typedValue.shipmentSpecId;
});

const hasUpdateOrCreateTemplatePermission = computed(() => {
  if (!selectedTemplate.value) {
    return authorization.hasAuthority('spotMarketRequestTemplate', 'create');
  } else {
    return authorization.hasAuthority('spotMarketRequestTemplate', 'update');
  }
});

const onSave = async () => {
  if (
    isEditingCarrierNotes.value ||
    isEditingLocationNotes.value ||
    savedTemplateModal.value.visible ||
    !(await validate())
  ) {
    return;
  }

  // Only update notes when changed and set to included. User could update notes
  // and then turn off include notes, and we don't want to update then.
  const hasNotesChanged = carrierNotes?.value !== internalCarrierNotes.value;
  const updatedNotes =
    hasNotesChanged && showEditableNotes.value
      ? internalCarrierNotes.value
      : undefined;

  emit('submit', internalValue.value, updatedNotes);
  isVisible.value = false;
};

const onCancel = () => {
  if (savedTemplateModal.value.visible) {
    return;
  }

  emit('cancel');
  isVisible.value = false;
};

const onSaveTemplate = () => {
  const newTemplate = new SpotMarketRequestTemplate({
    request: {
      scacs: internalValue.value.scacs,
      mode: internalValue.value.mode,
      equipmentType: internalValue.value.equipmentType,
      serviceType: internalValue.value.serviceType,
      expirationDuration: internalValue.value.expirationDuration,
      requireFuel: internalValue.value.requireFuel,
      includeCarrierNotes: internalValue.value.includeCarrierNotes,
      includeLocationNotes: internalValue.value.includeLocationNotes,
      upliftRule: internalValue.value.upliftRule,
    },
  });
  newTemplate.id = selectedTemplate.value?.id;
  newTemplate.name = selectedTemplate.value?.name;

  savedTemplateModal.value.template = newTemplate;
  savedTemplateModal.value.visible = true;
};

const onSaveTemplateConfirm = async (name: string) => {
  const template = new SpotMarketRequestTemplate({
    ...savedTemplateModal.value.template,
    name,
  });

  const isCreate =
    !template.id || savedTemplateModal.value.template.name !== name;
  if (isCreate) {
    template.id = undefined;

    const newTemplate = await createSpotMarketRequestTemplate(template);
    if (newTemplate) {
      selectedTemplate.value = newTemplate;
    }
  } else {
    const updatedTemplate = await updateSpotMarketRequestTemplate(template);
    if (updatedTemplate) {
      selectedTemplate.value = updatedTemplate;
    }
  }
};

const resetState = () => {
  v$.value.$reset();
  internalCarrierNotes.value = carrierNotes?.value;
};

const onDeleteTemplate = async (templateId: number) => {
  const wasDeleted = await deleteSpotMarketRequestTemplate(templateId);

  if (wasDeleted && selectedTemplate.value?.id === templateId) {
    selectedTemplate.value = undefined;
    const Clazz = Object.getPrototypeOf(inviteCommand.value).constructor;
    internalValue.value = new Clazz(inviteCommand.value);
    resetState();
  }
};

const setDefaultTemplate = async (template: SpotMarketRequestTemplate) => {
  const newDefaultTemplate =
    await setDefaultSpotMarketRequestTemplate(template);

  if (selectedTemplate.value?.id === newDefaultTemplate.id) {
    selectedTemplate.value = newDefaultTemplate;
  } else if (selectedTemplate.value?.id) {
    selectedTemplate.value.defaultTemplate = false;
  }
};

const removeDefaultTemplate = async (template: SpotMarketRequestTemplate) => {
  const previouslyDefaultTemplate =
    await removeDefaultSpotMarketRequestTemplate(template);

  if (selectedTemplate.value?.id === previouslyDefaultTemplate.id) {
    selectedTemplate.value = previouslyDefaultTemplate;
  }
};

const applyTemplate = (template: SpotMarketRequestTemplate) => {
  internalValue.value.scacs = template.request.scacs ?? [];
  internalValue.value.mode = template.request.mode;
  internalValue.value.equipmentType = template.request.equipmentType;
  internalValue.value.serviceType = template.request.serviceType;
  internalValue.value.expirationDuration = template.request.expirationDuration;
  internalValue.value.requireFuel = template.request.requireFuel;
  internalValue.value.includeCarrierNotes =
    template.request.includeCarrierNotes;
  internalValue.value.includeLocationNotes =
    template.request.includeLocationNotes;
  internalValue.value.upliftRule = template.request.upliftRule;
  resetState();
};

watch(
  isVisible,
  async (newVal: boolean) => {
    const Clazz = Object.getPrototypeOf(inviteCommand.value).constructor;
    internalValue.value = new Clazz(inviteCommand.value);

    if (newVal) {
      resetState();
      loadServiceLevels();
      loadEquipmentTypes();

      if (!spotMarketRequestTemplates.value.data) {
        await loadSpotMarketRequestTemplates();
      }

      selectedTemplate.value = defaultTemplate.value;
      if (defaultTemplate.value) {
        applyTemplate(defaultTemplate.value);
      }
    }
  },
  { immediate: true },
);

watch(selectedTemplate, (newVal: SpotMarketRequestTemplate | undefined) => {
  if (!newVal?.request) {
    return;
  }

  applyTemplate(newVal);
});

watch(
  () => carrierNotes?.value,
  (newVal) => {
    internalCarrierNotes.value = newVal;
  },
);

// watching carrier notes to reset location notes since we should not have location notes without carrier notes
watch(
  () => internalValue.value.includeCarrierNotes,
  (newVal) => {
    if (newVal === false) {
      internalValue.value.includeLocationNotes = false;
    }
  },
);
</script>

<style>
.invite-carriers-to-spot-market-modal {
  #invite-instructions {
    margin: 0 0 var(--sp-3);
  }

  .top-border {
    border-top: var(--divider);
    padding-top: var(--sp-3);
  }

  .invite-carriers-to-spot-market-modal-form {
    display: flex;
    flex-flow: column;
    gap: var(--gap-1);

    .template-selector {
      border-bottom: var(--divider);
      padding-bottom: var(--sp-3);
    }
  }
}
</style>
